import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faChartBar, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import ModalComponent from "src/core/components/modal/modal.component";
import PaginationComponent from "src/core/components/pagination/pagination.component";
import "../../../asset.scss";

interface AssetSelectionModalProps {
  showModal: boolean;
  handleClose: () => void;
  selectedAssets: string[];
  onRemoveAsset: (assetCode: string) => void;
  onClearAll: () => void;
  onViewAnalytics: () => void;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

const AssetSelectionModal: React.FC<AssetSelectionModalProps> = ({
  showModal,
  handleClose,
  selectedAssets,
  onRemoveAsset,
  onClearAll,
  onViewAnalytics,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
}) => {
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, selectedAssets.length);
  const paginatedAssets = selectedAssets.slice(startIndex, endIndex);
  const displayStart = selectedAssets.length > 0 ? startIndex + 1 : 0;

  const headerIcon = (
    <div className="asset-selection-header-icon">
      <FontAwesomeIcon icon={faListCheck} />
    </div>
  );

  return (
    <ModalComponent
      showModal={showModal}
      handleClose={handleClose}
      headerTitle="Asset Selection List"
      headerIcon={headerIcon}
      isViewMode={true}
      size="lg"
    >
      <div className="d-flex justify-content-end gap-2 mb-3">
        <B2Button
          variant="primary"
          onClick={onViewAnalytics}
          disabled={selectedAssets.length === 0}
        >
          <FontAwesomeIcon icon={faChartBar} className="me-2" />
          View Analytics
        </B2Button>
        <B2Button
          variant="danger"
          onClick={onClearAll}
          disabled={selectedAssets.length === 0}
        >
          Clear All
        </B2Button>
      </div>

      <div className="asset-selection-list-container">
        {/* Header row */}
        <div className="asset-selection-list-header">
          <div className="asset-selection-list-col asset-selection-list-col-code">ASSET CODE</div>
          <div className="asset-selection-list-col asset-selection-list-col-action">ACTION</div>
        </div>

        {/* Asset rows */}
        <div className="asset-selection-list-body">
          {paginatedAssets.length > 0 ? (
            paginatedAssets.map((assetCode) => (
              <div className="asset-selection-list-row" key={assetCode}>
                <div className="asset-selection-list-col asset-selection-list-col-code">
                  {assetCode}
                </div>
                <div className="asset-selection-list-col asset-selection-list-col-action">
                  <button
                    type="button"
                    className="asset-selection-remove-btn"
                    onClick={() => onRemoveAsset(assetCode)}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="asset-selection-empty text-center text-muted">
              No assets selected
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="asset-selection-footer">
          Showing {displayStart}-{endIndex} of {selectedAssets.length} entries
        </div>
        {selectedAssets.length > pageSize && (
          <PaginationComponent
            totalNumberOfRecords={selectedAssets.length}
            pageSize={pageSize}
            current={currentPage}
            onChangePage={onPageChange || (() => {})}
          />
        )}
      </div>
    </ModalComponent>
  );
};

export default AssetSelectionModal;
